/**
 * Multi-Language Support System for Hallyu4U
 * Supports: EN, KO, ZH-CN, JA
 */

class MultiLanguageSystem {
  constructor() {
    this.supportedLanguages = ['en', 'ko', 'zh-CN', 'ja'];
    this.defaultLanguage = 'en'; // Default to English
    this.currentLanguage = this.validateLanguage(this.getStoredLanguage() || 'en'); // Default to English instead of detecting
    this.translations = {};
    this.isInitialized = false;
    this.loadingPromise = null;
    this.translationCache = new Map();
    this.observers = new Set();
    
    this.init().catch(error => {
      console.error('MultiLanguageSystem initialization failed:', error);
      this.fallbackToEnglish();
    });
  }

  validateLanguage(lang) {
    return this.supportedLanguages.includes(lang) ? lang : 'en';
  }

  fallbackToEnglish() {
    this.currentLanguage = 'en';
    this.updateHtmlLang();
  }

  async init() {
    if (this.isInitialized) return;
    
    try {
      await this.loadTranslations();
      this.applyTranslations();
      this.setupLanguageSelector();
      this.updateHtmlLang();
      this.setupMutationObserver();
      this.isInitialized = true;
      
      // Dispatch ready event
      window.dispatchEvent(new CustomEvent('multilang:ready', {
        detail: { language: this.currentLanguage }
      }));
    } catch (error) {
      this.isInitialized = false;
      throw error;
    }
  }

  setupMutationObserver() {
    if (!window.MutationObserver) return;
    
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            this.translateElement(node);
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    this.observers.add(observer);
  }

  translateElement(element) {
    if (element.hasAttribute('data-i18n')) {
      const key = element.getAttribute('data-i18n');
      const translation = this.t(key);
      
      if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
        element.placeholder = translation;
      } else {
        element.textContent = translation;
      }
    }
    
    // Translate child elements
    element.querySelectorAll('[data-i18n]').forEach(child => {
      this.translateElement(child);
    });
  }

  // Detect user's browser language (but we'll default to English)
  detectLanguage() {
    // Always return English as default
    return 'en';
    
    // Original detection code (kept for reference but not used)
    // const browserLang = navigator.language || navigator.userLanguage;
    // const langCode = browserLang.toLowerCase();
    // if (langCode.startsWith('ko')) return 'ko';
    // if (langCode.startsWith('zh')) return 'zh-CN';
    // if (langCode.startsWith('ja')) return 'ja';
    // return 'en';
  }

  // Get stored language preference
  getStoredLanguage() {
    try {
      return localStorage.getItem('hallyu4u_language');
    } catch (error) {
      console.warn('Failed to access localStorage:', error);
      return null;
    }
  }

  // Store language preference
  setStoredLanguage(lang) {
    const validLang = this.validateLanguage(lang);
    try {
      localStorage.setItem('hallyu4u_language', validLang);
      this.currentLanguage = validLang;
    } catch (error) {
      console.warn('Failed to save language preference:', error);
      this.currentLanguage = validLang;
    }
  }

  // Update HTML lang attribute
  updateHtmlLang() {
    document.documentElement.lang = this.currentLanguage;
    document.documentElement.dir = this.isRTL(this.currentLanguage) ? 'rtl' : 'ltr';
  }

  // Check if language is RTL
  isRTL(lang) {
    return ['ar', 'he', 'fa', 'ur'].includes(lang);
  }

  // Load translations dynamically
  async loadTranslations() {
    try {
      // In production, these would be loaded from Shopify's locale files
      this.translations = {
        en: {
          // Header
          'header.welcome': 'Welcome to Hallyu4U',
          'header.login': 'Login',
          'header.register': 'Sign Up',
          'header.myaccount': 'My Account',
          'header.warehouse': 'Warehouse',
          'header.shipping': 'Shipping',
          'header.logout': 'Logout',
          
          // Navigation
          'nav.home': 'Home',
          'nav.howitworks': 'How It Works',
          'nav.pricing': 'Pricing',
          'nav.shopping': 'Shopping Guide',
          'nav.faq': 'FAQ',
          'nav.contact': 'Contact',
          
          // Hero Section
          'hero.title': 'Your Gateway to Korean Shopping',
          'hero.subtitle': 'Shop from any Korean store and ship worldwide with ease',
          'hero.cta.start': 'Get Started',
          'hero.cta.learn': 'Learn More',
          
          // Features
          'features.storage': 'Free Storage',
          'features.storage.desc': '45 days free storage for all packages',
          'features.consolidation': 'Package Consolidation',
          'features.consolidation.desc': 'Save up to 70% on shipping costs',
          'features.worldwide': 'Worldwide Shipping',
          'features.worldwide.desc': 'We ship to 220+ countries',
          
          // How it works
          'how.title': 'How Hallyu4U Works',
          'how.subtitle': 'Get Korean products in 3 simple steps',
          'how.step1.title': 'Sign Up & Get Address',
          'how.step1.desc': 'Register for free and receive your Korean shipping address',
          'how.step2.title': 'Shop & Store',
          'how.step2.desc': 'Shop from Korean stores and ship to our warehouse',
          'how.step3.title': 'Consolidate & Save',
          'how.step3.desc': 'Combine packages and save up to 70% on shipping',
          
          // Dashboard
          'dashboard.welcome': 'Welcome to Your Dashboard',
          'dashboard.address': 'Your Korean Address',
          'dashboard.copy': 'Copy Address',
          'dashboard.copied': 'Address Copied!',
          'dashboard.packages': 'Total Packages',
          'dashboard.storage': 'In Storage',
          'dashboard.shipped': 'Shipped',
          'dashboard.pending': 'Pending',
          
          // Package Management
          'package.search': 'Search packages...',
          'package.filter.all': 'All',
          'package.filter.storage': 'In Storage',
          'package.filter.requested': 'Requested',
          'package.filter.shipped': 'Shipped',
          'package.sort.newest': 'Newest First',
          'package.sort.oldest': 'Oldest First',
          'package.consolidate': 'Consolidate Selected',
          'package.request': 'Request Shipping',
          'package.track': 'Track Package',
          
          // Footer
          'footer.about': 'About Us',
          'footer.terms': 'Terms of Service',
          'footer.privacy': 'Privacy Policy',
          'footer.contact': 'Contact Us',
          'footer.newsletter': 'Subscribe to Newsletter',
          'footer.rights': 'All rights reserved'
        },
        ko: {
          // Header
          'header.welcome': 'Hallyu4U에 오신 것을 환영합니다',
          'header.login': '로그인',
          'header.register': '회원가입',
          'header.myaccount': '내 계정',
          'header.warehouse': '창고',
          'header.shipping': '배송',
          'header.logout': '로그아웃',
          
          // Navigation
          'nav.home': '홈',
          'nav.howitworks': '이용방법',
          'nav.pricing': '요금안내',
          'nav.shopping': '쇼핑가이드',
          'nav.faq': '자주 묻는 질문',
          'nav.contact': '문의하기',
          
          // Hero Section
          'hero.title': '한국 쇼핑의 관문',
          'hero.subtitle': '모든 한국 온라인 쇼핑몰에서 쇼핑하고 전 세계로 배송받으세요',
          'hero.cta.start': '시작하기',
          'hero.cta.learn': '자세히 알아보기',
          
          // Features
          'features.storage': '무료 보관',
          'features.storage.desc': '모든 패키지 45일 무료 보관',
          'features.consolidation': '패키지 통합',
          'features.consolidation.desc': '배송비 최대 70% 절약',
          'features.worldwide': '전 세계 배송',
          'features.worldwide.desc': '220개국 이상 배송 가능',
          
          // How it works
          'how.title': '한류포유 이용방법',
          'how.subtitle': '간단한 3단계로 한국 상품을 받아보세요',
          'how.step1.title': '회원가입 & 주소받기',
          'how.step1.desc': '무료 회원가입 후 전용 한국 주소를 받으세요',
          'how.step2.title': '쇼핑 & 보관',
          'how.step2.desc': '한국 온라인 쇼핑몰에서 쇼핑하고 우리 창고로 배송하세요',
          'how.step3.title': '통합배송 & 절약',
          'how.step3.desc': '여러 패키지를 하나로 통합해서 배송비를 절약하세요'
        },
        'zh-CN': {
          // Header
          'header.welcome': '欢迎来到Hallyu4U',
          'header.login': '登录',
          'header.register': '注册',
          'header.myaccount': '我的账户',
          'header.warehouse': '仓库',
          'header.shipping': '运输',
          'header.logout': '退出',
          
          // Navigation
          'nav.home': '主页',
          'nav.howitworks': '使用方法',
          'nav.pricing': '价格',
          'nav.shopping': '购物指南',
          'nav.faq': '常见问题',
          'nav.contact': '联系我们',
          
          // Hero Section
          'hero.title': '您的韩国购物门户',
          'hero.subtitle': '从任何韩国商店购物并轻松运送到全球',
          'hero.cta.start': '开始使用',
          'hero.cta.learn': '了解更多'
        },
        ja: {
          // Header
          'header.welcome': 'Hallyu4Uへようこそ',
          'header.login': 'ログイン',
          'header.register': '新規登録',
          'header.myaccount': 'マイアカウント',
          'header.warehouse': '倉庫',
          'header.shipping': '配送',
          'header.logout': 'ログアウト',
          
          // Navigation
          'nav.home': 'ホーム',
          'nav.howitworks': '使い方',
          'nav.pricing': '料金',
          'nav.shopping': 'ショッピングガイド',
          'nav.faq': 'よくある質問',
          'nav.contact': 'お問い合わせ',
          
          // Hero Section
          'hero.title': '韓国ショッピングへのゲートウェイ',
          'hero.subtitle': '韓国のオンラインストアから簡単に世界中へ配送',
          'hero.cta.start': '始める',
          'hero.cta.learn': '詳しく見る'
        }
      };
    } catch (error) {
      console.error('Failed to load translations:', error);
    }
  }

  // Get translation for a key with optional interpolation
  t(key, params = {}) {
    // Check cache first
    const cacheKey = `${this.currentLanguage}:${key}:${JSON.stringify(params)}`;
    if (this.translationCache.has(cacheKey)) {
      return this.translationCache.get(cacheKey);
    }
    
    const translations = this.translations[this.currentLanguage] || this.translations.en;
    let translation = translations[key];
    
    if (!translation) {
      // Try fallback to English
      translation = this.translations.en?.[key] || key;
    }
    
    // Interpolate parameters
    if (params && Object.keys(params).length > 0) {
      translation = this.interpolate(translation, params);
    }
    
    // Cache the result
    this.translationCache.set(cacheKey, translation);
    
    return translation;
  }

  interpolate(str, params) {
    return str.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return params[key] !== undefined ? params[key] : match;
    });
  }

  // Apply translations to DOM elements
  applyTranslations() {
    const startTime = performance.now();
    
    try {
      // Regular text elements
      document.querySelectorAll('[data-i18n]').forEach(element => {
        const key = element.getAttribute('data-i18n');
        const params = this.extractParams(element);
        const translation = this.t(key, params);
        
        if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
          element.placeholder = translation;
        } else if (element.tagName === 'IMG') {
          element.alt = translation;
        } else if (element.tagName === 'A' || element.tagName === 'BUTTON') {
          element.setAttribute('aria-label', translation);
          element.textContent = translation;
        } else {
          element.textContent = translation;
        }
      });
      
      // HTML content elements (sanitized)
      document.querySelectorAll('[data-i18n-html]').forEach(element => {
        const key = element.getAttribute('data-i18n-html');
        const params = this.extractParams(element);
        const translation = this.t(key, params);
        element.innerHTML = this.sanitizeHtml(translation);
      });
      
      // Title attributes
      document.querySelectorAll('[data-i18n-title]').forEach(element => {
        const key = element.getAttribute('data-i18n-title');
        element.title = this.t(key);
      });
      
      const endTime = performance.now();
      console.debug(`Translations applied in ${(endTime - startTime).toFixed(2)}ms`);
    } catch (error) {
      console.error('Failed to apply translations:', error);
    }
  }

  extractParams(element) {
    const params = {};
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.startsWith('data-i18n-param-')) {
        const paramName = attr.name.replace('data-i18n-param-', '');
        params[paramName] = attr.value;
      }
    });
    return params;
  }

  sanitizeHtml(html) {
    // Basic HTML sanitization
    const tempDiv = document.createElement('div');
    tempDiv.textContent = html;
    return tempDiv.innerHTML;
  }

  // Setup language selector
  setupLanguageSelector() {
    const selectors = document.querySelectorAll('[data-language-selector]');
    
    selectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        this.changeLanguage(e.target.value);
      });
      
      // Set current language
      selector.value = this.currentLanguage;
    });
    
    // Setup language buttons
    document.querySelectorAll('[data-lang-btn]').forEach(btn => {
      btn.addEventListener('click', () => {
        const lang = btn.getAttribute('data-lang-btn');
        this.changeLanguage(lang);
      });
      
      // Highlight active language
      if (btn.getAttribute('data-lang-btn') === this.currentLanguage) {
        btn.classList.add('active');
      }
    });
  }

  // Change language
  async changeLanguage(lang) {
    const validLang = this.validateLanguage(lang);
    
    if (validLang === this.currentLanguage) {
      console.debug('Language already set to:', validLang);
      return;
    }
    
    const previousLang = this.currentLanguage;
    
    try {
      this.setStoredLanguage(validLang);
      
      // Clear translation cache
      this.translationCache.clear();
      
      // Ensure translations are loaded
      if (!this.translations[validLang]) {
        await this.loadTranslations();
      }
      
      this.updateHtmlLang();
      this.applyTranslations();
      this.updateLanguageSelectors(validLang);
      
      // Update URL if needed (for SEO)
      this.updateURL(validLang);
      
      // Trigger custom event
      window.dispatchEvent(new CustomEvent('languageChanged', { 
        detail: { 
          language: validLang,
          previousLanguage: previousLang
        } 
      }));
      
      // Reload certain dynamic content if needed
      await this.reloadDynamicContent();
    } catch (error) {
      console.error('Failed to change language:', error);
      // Rollback
      this.currentLanguage = previousLang;
      throw error;
    }
  }

  updateLanguageSelectors(lang) {
    // Update select elements
    document.querySelectorAll('[data-language-selector]').forEach(selector => {
      selector.value = lang;
    });
    
    // Update button states
    document.querySelectorAll('[data-lang-btn]').forEach(btn => {
      const btnLang = btn.getAttribute('data-lang-btn');
      if (btnLang === lang) {
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
  }

  // Update URL with language parameter
  updateURL(lang) {
    const url = new URL(window.location);
    
    if (lang === 'en') {
      url.searchParams.delete('lang');
    } else {
      url.searchParams.set('lang', lang);
    }
    
    window.history.replaceState({}, '', url);
  }

  // Reload dynamic content
  async reloadDynamicContent() {
    try {
      // Reload Shopify cart count, prices, etc.
      if (window.Shopify && window.Shopify.currency) {
        // Update currency display based on language
        const currencyMap = {
          'en': 'USD',
          'ko': 'KRW',
          'zh-CN': 'CNY',
          'ja': 'JPY'
        };
        
        const newCurrency = currencyMap[this.currentLanguage] || 'USD';
        
        // Notify other systems about language change
        if (window.Hallyu4U) {
          window.Hallyu4U.config.locale = this.currentLanguage;
        }
        
        // Update prices on page
        this.updatePriceDisplay(newCurrency);
      }
      
      // Update date displays
      document.querySelectorAll('[data-date]').forEach(element => {
        const dateStr = element.getAttribute('data-date');
        if (dateStr) {
          const date = new Date(dateStr);
          element.textContent = this.formatDate(date);
        }
      });
    } catch (error) {
      console.error('Failed to reload dynamic content:', error);
    }
  }

  updatePriceDisplay(currency) {
    document.querySelectorAll('[data-price]').forEach(element => {
      const price = parseFloat(element.getAttribute('data-price'));
      if (!isNaN(price)) {
        element.textContent = this.formatCurrency(price, currency);
      }
    });
  }

  // Format currency based on language
  formatCurrency(amount, currency = 'USD') {
    const locale = this.currentLanguage === 'zh-CN' ? 'zh-CN' : this.currentLanguage;
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  // Format date based on language
  formatDate(date, options = {}) {
    const locale = this.currentLanguage === 'zh-CN' ? 'zh-CN' : this.currentLanguage;
    
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    }).format(date);
  }
}

// Initialize on DOM ready
(function() {
  'use strict';
  
  function initMultiLang() {
    try {
      if (!window.multiLang) {
        window.multiLang = new MultiLanguageSystem();
      }
    } catch (error) {
      console.error('Failed to initialize MultiLanguageSystem:', error);
    }
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMultiLang);
  } else {
    initMultiLang();
  }
})();